<script setup lang="ts">
  import { computed, inject, type Ref, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import {
    ClipboardPen,
    ChevronsLeft,
    ChevronDown,
    ChevronUp,
    CircleCheck,
    Circle,
    XCircle,
    Repeat
  } from 'lucide-vue-next'
  import { ROUTES } from '@/router/routes'
  import { type ClientOrganisation } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import clientPortalOrganisationCard from '@/components/clientPortal/clientPortalOrganisationCardV3.vue'
  import { useQuestionnaireStore } from '@/store/questionnaireStore.ts'
  import type { Page } from '@/types'
  import { useQuestionnaire } from '@/composables/useQuestionnaire'
  import ScrollArea from '@/components/ui/scroll-area/ScrollArea.vue'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

  const route = useRoute()
  const router = useRouter()
  const { t } = useTranslation({
    en: {
      requiredActions: 'Required Actions',
      questionnaire: 'Questionnaire',
      pdfDocument: 'PDF Document',
      loadingQuestionnaireData: 'Loading questionnaire data...',
      poweredBy: 'Powered by',
      overview: 'Overview',
      repeatableSectionTooltip: 'This section can be repeated multiple times'
    },
    de: {
      requiredActions: 'Erforderliche Aktionen',
      questionnaire: 'Fragebogen',
      pdfDocument: 'PDF-Dokument',
      loadingQuestionnaireData: 'Lade Fragebogen-Daten...',
      poweredBy: 'Powered by',
      overview: 'Übersicht',
      repeatableSectionTooltip: 'Dieser Abschnitt kann mehrfach wiederholt werden'
    }
  })

  const org = inject<Ref<ClientOrganisation | null>>('org')
  const questionnaire = inject<Ref<any | null>>('questionnaire', ref(null))
  const questionnaireStore = useQuestionnaireStore()

  const { fieldValidationErrors, saveFieldData } = useQuestionnaire()

  const isQuestionnaireRoute = computed(() => {
    return route.path.includes('/questionnaires/')
  })

  const navigationItems = [
    {
      id: 'home',
      label: t('requiredActions'),
      icon: ClipboardPen,
      route: ROUTES.CLIENT_PORTAL.HOME,
      isActive: computed(() => route.path === ROUTES.CLIENT_PORTAL.HOME)
    }
  ]

  // Questionnaire specific logic
  const openSections = ref<string[]>([])
  const rawSchema = computed(() => {
    if (questionnaire.value?.schema) {
      return questionnaire.value.schema as Page[]
    }
    return []
  })

  const activeField = computed(() => questionnaireStore.activeFieldId)

  const toggleSection = (sectionUuid: string) => {
    if (openSections.value.includes(sectionUuid)) {
      openSections.value = openSections.value.filter((uuid) => uuid !== sectionUuid)
    } else {
      openSections.value = [sectionUuid]
    }
  }

  const processChange = async (uuid: string, value: any) => {
    if (!questionnaire.value) return

    await saveFieldData(uuid, value, questionnaire.value)
  }

  const handleItemClick = async (uuid: string) => {
    if (isQuestionnaireRoute.value) {
      // Save current field data before jumping to new field
      const currentFieldId = questionnaireStore.activeFieldId
      const currentFieldValue = questionnaireStore.formData[currentFieldId]

      // Only save if the current field has a value
      if (currentFieldId && currentFieldValue !== undefined && currentFieldValue !== '') {
        // Trigger validation for the current field before saving
        // This ensures we have the latest validation state
        await processChange(currentFieldId, currentFieldValue)
      }

      questionnaireStore.jumpToField(uuid)

      if (window.innerWidth < 768) {
        emit('toggle')
      }
    }
  }

  const isQuestionAnswered = (uuid: string) => {
    const value = questionnaireStore.formData[uuid]

    // Find the field in the schema to check if empty string is a valid choice
    const field = rawSchema.value
      .flatMap((section) => section.items)
      .find((item) => item.uuid === uuid)

    const hasEmptyAsChoice = field && field.choices && field.choices.includes('')

    const hasValue = (() => {
      if (typeof value === 'object' && value !== null) {
        return Object.keys(value).length > 0
      }
      return (
        value !== undefined &&
        value !== null &&
        (hasEmptyAsChoice || value !== '') &&
        (Array.isArray(value) ? value.length > 0 : true)
      )
    })()

    if (!hasValue) {
      return false
    }

    const hasValidationError = fieldValidationErrors.value[uuid]

    return hasValue && !hasValidationError
  }

  const isQuestionInvalid = (uuid: string) => {
    const value = questionnaireStore.formData[uuid]

    // Find the field in the schema to check if empty string is a valid choice
    const field = rawSchema.value
      .flatMap((section) => section.items)
      .find((item) => item.uuid === uuid)

    const hasEmptyAsChoice = field && field.choices && field.choices.includes('')

    const hasValue = (() => {
      if (typeof value === 'object' && value !== null) {
        return Object.keys(value).length > 0
      }
      return (
        value !== undefined &&
        value !== null &&
        (hasEmptyAsChoice || value !== '') &&
        (Array.isArray(value) ? value.length > 0 : true)
      )
    })()

    const hasValidationError = fieldValidationErrors.value[uuid]
    return hasValue && hasValidationError
  }

  const isActiveField = (uuid: string) => {
    return uuid === activeField.value
  }

  const updateOpenSections = () => {
    if (!questionnaire.value) {
      return
    }

    if (activeField.value) {
      const currentSection = rawSchema.value.find((section) =>
        section.items.some((item) => item.uuid === activeField.value)
      )
      if (currentSection) {
        // Use uuid if available, otherwise fall back to page
        const sectionId = (currentSection as any).uuid || currentSection.page?.toString() || ''
        openSections.value = [sectionId]
      } else {
        const firstSection = rawSchema.value[0]
        const sectionId = (firstSection as any)?.uuid || firstSection?.page?.toString() || ''
        openSections.value = rawSchema.value.length > 0 ? [sectionId] : []
      }
    } else {
      // If no active field, open the first section
      const firstSection = rawSchema.value[0]
      const sectionId = (firstSection as any)?.uuid || firstSection?.page?.toString() || ''
      openSections.value = rawSchema.value.length > 0 ? [sectionId] : []
    }
  }

  watch(
    () => questionnaire.value,
    (newQuestionnaire) => {
      if (isQuestionnaireRoute.value && newQuestionnaire && newQuestionnaire.schema) {
        updateOpenSections()
      }
    },
    { immediate: true }
  )

  watch(
    () => activeField.value,
    () => {
      if (isQuestionnaireRoute.value && questionnaire.value) {
        updateOpenSections()
      }
    }
  )

  if (isQuestionnaireRoute.value && questionnaire.value && questionnaire.value.schema) {
    updateOpenSections()
  }

  const handleNavigation = (item: any) => {
    if (item.route) {
      const urlParams = new URLSearchParams(window.location.search)
      const accessToken = urlParams.get('access_token')

      const url = accessToken ? `${item.route}?access_token=${accessToken}` : item.route
      router.push(url)
    }
  }

  const emit = defineEmits(['toggle'])
</script>

<template>
  <div
    data-cy="Sidebar"
    class="flex h-full w-[250px] flex-col overflow-y-hidden border-r border-gray-200 bg-white"
  >
    <div class="flex h-16 items-center justify-end px-6">
      <button
        data-cy="SidebarCloseButton"
        class="rounded-md p-1 transition-colors hover:bg-gray-100"
        @click="emit('toggle')"
      >
        <ChevronsLeft class="h-5 w-5 text-gray-600" />
      </button>
    </div>

    <nav class="flex flex-1 flex-col overflow-y-hidden px-4">
      <template v-if="!isQuestionnaireRoute">
        <div
          v-for="item in navigationItems"
          :key="item.id"
          class="flex cursor-pointer items-center rounded-md px-3 py-2 text-sm font-normal transition-colors"
          :class="[
            item.isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          ]"
          @click="handleNavigation(item)"
        >
          <component
            :is="item.icon"
            class="mr-3 h-5 w-5"
            :class="item.isActive ? 'text-gray-900' : 'text-gray-500'"
          />
          {{ item.label }}
        </div>
      </template>

      <!-- Questionnaire sections -->
      <div
        v-if="isQuestionnaireRoute && !questionnaire"
        class="mb-4"
      >
        <div class="mb-2 text-xs text-gray-500">
          {{ t('loadingQuestionnaireData') }}
        </div>
      </div>

      <!-- Questionnaire title -->
      <div
        v-if="isQuestionnaireRoute && questionnaire"
        class="mb-4 flex-shrink-0"
      >
        <h2 class="mb-3 text-lg font-semibold text-gray-900">
          {{ t('overview') }}
        </h2>
      </div>

      <template v-if="isQuestionnaireRoute && questionnaire">
        <ScrollArea class="min-h-0 flex-1">
          <div
            v-for="section in rawSchema"
            :key="(section as any).uuid || section.page?.toString()"
            class="mb-4"
          >
            <div
              class="flex cursor-pointer items-start justify-between rounded-lg bg-white p-3 duration-300
                hover:bg-gray-50"
              @click="toggleSection((section as any).uuid || section.page?.toString() || '')"
            >
              <div class="flex min-w-0 flex-1 items-center gap-1.5">
                <TooltipProvider v-if="(section as any).repeatable">
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <div
                        class="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-blue-100"
                      >
                        <Repeat class="h-3 w-3 text-blue-600" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      variant="secondary"
                      :show-arrow="true"
                    >
                      {{ t('repeatableSectionTooltip') }}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <h3 class="break-words text-sm font-medium text-gray-900">
                  {{ section.title }}
                </h3>
              </div>
              <ChevronDown
                v-if="
                  !openSections.includes((section as any).uuid || section.page?.toString() || '')
                "
                class="h-5 w-5 text-gray-500"
              />
              <ChevronUp
                v-else
                class="h-5 w-5 text-gray-500"
              />
            </div>

            <transition
              enter-active-class="transition-all duration-300 ease-in-out"
              enter-from-class="opacity-0 transform -translate-y-2"
              enter-to-class="opacity-100 transform translate-y-0"
              leave-active-class="transition-all duration-300 ease-in-out"
              leave-from-class="opacity-100 transform translate-y-0"
              leave-to-class="opacity-0 transform -translate-y-2"
            >
              <div
                v-if="
                  openSections.includes((section as any).uuid || section.page?.toString() || '')
                "
                class="ml-4 mt-2"
              >
                <div
                  v-for="item in section.items"
                  :key="item.uuid"
                  :class="[
                    `flex cursor-pointer items-center rounded-r-md border-l-2 border-gray-200 px-3 py-2 text-sm
                    font-normal transition-colors`,
                    isActiveField(item.uuid)
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  ]"
                  :data-cy="`SidebarQuestionItem-${item.uuid}`"
                  @click="handleItemClick(item.uuid)"
                >
                  <component
                    :is="CircleCheck"
                    v-if="isQuestionAnswered(item.uuid)"
                    class="mr-3 h-5 w-5 flex-shrink-0 text-green-500"
                  />
                  <component
                    :is="XCircle"
                    v-else-if="isQuestionInvalid(item.uuid)"
                    class="mr-3 h-5 w-5 flex-shrink-0 text-red-500"
                  />
                  <component
                    :is="Circle"
                    v-else
                    class="mr-3 h-5 w-5 flex-shrink-0 text-gray-300"
                  />
                  <span class="break-words">
                    {{ item.label }}
                  </span>
                </div>
              </div>
            </transition>
          </div>
        </ScrollArea>
      </template>
    </nav>

    <div
      v-if="org"
      class="p-3"
    >
      <clientPortalOrganisationCard />
    </div>

    <div class="flex w-full items-center justify-center bg-white py-1">
      <div class="font-[Inter] text-sm font-medium tracking-tight text-gray-600">
        {{ t('poweredBy') }}
      </div>
      <img
        class="ml-[6px] h-[24px] w-[56px]"
        src="https://s3-eu-central-1.ionoscloud.com/jupus/images/logo.svg"
        alt="Jupus Logo"
      />
    </div>
  </div>
</template>
